/**
 * Two derived animations that every structure gets for free, whichever draw
 * path produced it (doc 17 §6/§7 — feedback the player can read at a glance):
 *
 * **The rise.** A new building is not stamped onto the seabed, it comes up out
 * of it: the sprite is revealed bottom-up behind a bright construction line,
 * with bubbles escaping the cut. Purely cosmetic — the sim placed the thing the
 * instant it was paid for, and a tower fires on its first frame.
 *
 * **The upgrade flash.** Buying a tier flashes the silhouette in the tower's
 * signature light for two frames, so the purchase is acknowledged on the model
 * and not only in the panel.
 *
 * Both are post-passes over a finished surface: no entity has to author them,
 * which is the same reason interaction states are derived (doc 16 §1).
 */

import { SEMANTIC } from '../render/palette.ts';
import { mix, PixelSurface, TRANSPARENT, type Color } from '../render/surface.ts';

/** Frames the rise takes, at 30 Hz — ~450 ms (doc 17 §6 slide budget). */
export const BUILD_TICKS = 14;
/** Frames an upgrade flash lasts. */
export const FLASH_TICKS = 6;

/**
 * Reveal `s` from the anchor line upward. `p` is 0..1; at 0 nothing shows, at 1
 * the sprite is untouched. `anchorY` is the sprite's ground line — everything
 * above it is what rises.
 */
export function applyRise(s: PixelSurface, anchorY: number, p: number): void {
  if (p >= 1) return;
  const b = s.inkBounds();
  if (!b) return;
  const top = Math.min(b.y0, anchorY);
  const height = Math.max(1, anchorY - top);
  const cut = Math.round(anchorY - height * p);

  // erase everything still underground
  for (let y = 0; y < cut; y++) {
    for (let x = 0; x < s.width; x++) {
      if (s.alphaAt(x, y) > 0) s.px(x, y, TRANSPARENT, 'set');
    }
  }
  if (cut <= top) return;

  // the construction line: the lit edge of what has surfaced so far
  let x0 = s.width;
  let x1 = -1;
  for (let x = 0; x < s.width; x++) {
    if (s.alphaAt(x, cut) > 0 || s.alphaAt(x, cut + 1) > 0) {
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
    }
  }
  if (x1 < x0) return;
  s.hline(x0, x1, cut, SEMANTIC.good, 'set');
  // and the bubbles it lets go of, on deterministic lanes
  const span = Math.max(1, x1 - x0);
  for (let k = 0; k < 3; k++) {
    const bx = x0 + ((k * 37 + Math.round(p * 23)) % span);
    const by = cut - 2 - ((k * 5 + Math.round(p * 17)) % 6);
    if (by >= 0) s.px(bx, by, mix(SEMANTIC.good, SEMANTIC.ink, 0.5));
  }
}

/**
 * Flash every inked pixel toward `color`. `k` is 0..1 — 1 is a full white-out,
 * 0 leaves the sprite alone. Quantised to four steps so it stays pixel art.
 */
export function applyFlash(s: PixelSurface, k: number, color: Color = SEMANTIC.ink): void {
  const step = Math.round(Math.max(0, Math.min(1, k)) * 4) / 4;
  if (step <= 0) return;
  for (let y = 0; y < s.height; y++) {
    for (let x = 0; x < s.width; x++) {
      const a = s.alphaAt(x, y);
      if (a === 0) continue;
      s.px(x, y, mix(s.get(x, y), color, step), 'set');
    }
  }
}
